import { Emitter } from '@pixi/particle-emitter';
import { Container, InteractionEvent } from 'pixi.js';
import { MainParticle } from '../../constant';
import { DrawingPatternData, Point } from '../../interface';
import { clearBitmapBuffer, compareBitmapBuffer, drawLinesBitmapBuffer, getAngleCount } from '../../util';
import DrawingPattern from './Pattern';

class Canvas extends Container {
  public points: Array<Point> = [];
  private isDragBegin: boolean = false;
  private bitmapBuffer: Array<boolean>;
  private lineColor: number = 0x000000;
  private targetLineColor: number = 0x000000;
  private particleEmitter: Emitter;

  constructor() {
    super();
    this.interactive = false;
    this.interactiveChildren = false;
    this.bitmapBuffer = new Array(16 * 16).fill(false);
    this.particleEmitter = new Emitter(this, MainParticle);
    this.particleEmitter.emit = false;
  }

  public update(elapsed: number): void {
    this.particleEmitter.update(elapsed * 0.002);

    this.updateLineColor();
    clearBitmapBuffer(this.bitmapBuffer);
    drawLinesBitmapBuffer(this.points, this.bitmapBuffer, 16, 16, 1);
  }

  public pointerdown = (event: InteractionEvent) => {
    if (!this.isDragBegin) {
      this.points = [
        {
          x: event.data.global.x,
          y: event.data.global.y,
        },
      ];
      this.isDragBegin = true;
      this.particleEmitter.spawnPos.x = event.data.global.x;
      this.particleEmitter.spawnPos.y = event.data.global.y;
      this.particleEmitter.emit = true;
    } else {
      this.isDragBegin = false;
      this.particleEmitter.emit = false;
    }
  };

  public pointermove = (event: InteractionEvent) => {
    const LINE_LENGTH_LIMIT = 200;
    this.particleEmitter.spawnPos.x = event.data.global.x;
    this.particleEmitter.spawnPos.y = event.data.global.y;

    if (this.isDragBegin) {
      const diff = Math.sqrt(
        (this.points[this.points.length - 1].x - event.data.global.x) ** 2 +
          (this.points[this.points.length - 1].y - event.data.global.y) ** 2
      );

      if (diff > 3 && this.points.length < LINE_LENGTH_LIMIT) {
        this.points.push({
          x: event.data.global.x,
          y: event.data.global.y,
        });
        this.checkSimilar();
      } else if (this.points.length >= LINE_LENGTH_LIMIT) {
        this.pointerup(event);
      }
    }
  };

  public pointerup = (event: InteractionEvent) => {
    this.isDragBegin = false;
    this.particleEmitter.emit = false;
    this.checkSimilar(false);
  };

  private updateLineColor(): void {
    const [r, g, b] = [(this.lineColor >> 16) & 255, (this.lineColor >> 8) & 255, this.lineColor & 255];
    const [tr, tg, tb] = [
      (this.targetLineColor >> 16) & 255,
      (this.targetLineColor >> 8) & 255,
      this.targetLineColor & 255,
    ];
    const [dr, dg, db] = [tr - r, tg - g, tb - b];
    this.lineColor = (Math.round(r + dr * 0.3) << 16) + (Math.round(g + dg * 0.3) << 8) + Math.round(b + db * 0.3);
  }

  private checkSimilar(isPeak: boolean = true): void {
    let result: { similarity: number; patternData?: DrawingPatternData } = { similarity: 0 };

    const angleCount = getAngleCount(this.points);
    const anglePatterns = DrawingPattern.getAnglePatterns(angleCount);

    for (const anglePattern of anglePatterns) {
      for (const answer of anglePattern.answers) {
        const similarity = compareBitmapBuffer(this.bitmapBuffer, answer);

        if (result.similarity < similarity) {
          result.similarity = similarity;
          result.patternData = anglePattern;
        }
      }
    }

    if (result.similarity && result.similarity >= (result?.patternData?.similarityPassLimit || 0)) {
      this.targetLineColor = result?.patternData?.color || 0x000000;

      if (!isPeak) {
        this.emit('drawing-pattern-found', result);
      }
    } else {
      this.targetLineColor = 0x000000;
    }
  }
}

export default Canvas;
